import { useEffect, useState, type FormEvent } from "react";
import { Navigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { BookCondition } from "@passa-a-pagina/shared";
import { isValidIsbn } from "@passa-a-pagina/shared";
import { api, ApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useAdminSession } from "../../lib/AdminSessionContext";
import { IsbnScanner } from "../../components/IsbnScanner";
import { BookUploadModal } from "../../components/BookUploadModal";
import { PhotoImportModal } from "../../components/PhotoImportModal";
import { UserPicker } from "../../components/UserPicker";
import { IconBarcode } from "../../components/icons";
import "./inventory.css";

interface ConfirmedBook {
  isbn: string | null;
  manual: boolean;
  metadata: {
    title: string;
    author: string | null;
    description: string | null;
    genre: string | null;
    coverUrl: string | null;
    language: string | null;
  };
}

interface IsbnLookupResp {
  isbn: string;
  metadata: ConfirmedBook["metadata"];
  // "local" → metadata came from our own books table (a prior scan).
  // "google_books" → fresh from the Google Books API.
  source?: "local" | "google_books";
}

export function AdminInventory() {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const qc = useQueryClient();
  const { visitorEmail, setVisitor } = useAdminSession();
  const [isbn, setIsbn] = useState("");
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [confirmed, setConfirmed] = useState<ConfirmedBook | null>(null);
  const [donorEmail, setDonorEmail] = useState(visitorEmail ?? "");
  // "Sem créditos": anonymous donation — no email, no point credited.
  const [noCredit, setNoCredit] = useState(false);
  const [flash, setFlash] = useState<{ type: "ok" | "err"; msg: string } | null>(null);
  const [errors, setErrors] = useState<{ isbn?: string; donorEmail?: string }>({});
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  // True once a Google Books lookup has failed for the current ISBN. Flips the
  // title/author inputs from disabled to enabled so the admin can fall through
  // to manual entry without losing the scanned ISBN.
  const [lookupFailed, setLookupFailed] = useState(false);

  // Keep the local email in sync with the session pill — if the admin clears
  // the visit from the pill, the donor field clears too.
  useEffect(() => {
    setDonorEmail(visitorEmail ?? "");
  }, [visitorEmail]);

  const lookup = useQuery<IsbnLookupResp, ApiError>({
    queryKey: ["admin", "isbn", isbn],
    queryFn: () => api<IsbnLookupResp>(`/api/admin/isbn-lookup/${encodeURIComponent(isbn)}`),
    enabled: false,
    retry: false,
  });

  type SubmitInput = {
    isbn?: string;
    title?: string;
    author?: string;
    description?: string;
    genre?: string;
    coverUrl?: string;
    condition: BookCondition;
    donorEmail?: string;
    noCredit?: boolean;
  };

  const submit = useMutation({
    mutationFn: (input: SubmitInput) =>
      api<{
        book: { id: string; title: string };
        donor: { email: string | null; pointCredited: number };
      }>("/api/admin/books", { method: "POST", body: input }),
    onSuccess: (res) => {
      const credited = res.donor?.email;
      setFlash({
        type: "ok",
        msg: credited
          ? `"${res.book.title}" adicionado. +1 ponto creditado a ${credited}.`
          : `"${res.book.title}" adicionado sem créditos (doador anónimo).`,
      });
      // Persist the visitor on the session so subsequent scans in this batch
      // don't have to retype the email. Only when a real donor was used.
      if (credited) {
        setVisitor(credited);
        // Belt-and-suspenders: also reset the donor field directly here so
        // the next scan's confirm form is guaranteed to render pre-filled,
        // regardless of effect ordering / batching.
        setDonorEmail(credited);
      }
      setIsbn("");
      setTitle("");
      setAuthor("");
      setConfirmed(null);
      setLookupFailed(false);
      // Reset the noCredit checkbox so it doesn't silently apply to the next book.
      setNoCredit(false);
      qc.invalidateQueries({ queryKey: ["books"] });
    },
    onError: (err: ApiError) => {
      setFlash({ type: "err", msg: err.message });
    },
  });

  if (loading) return <div className="container"><p>A carregar…</p></div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  async function onProcess(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    setFlash(null);
    setLookupError(null);

    const isbnClean = isbn.replace(/[-\s]/g, "").toUpperCase();
    const titleClean = title.trim();

    // Fallback path: ISBN was scanned but the prior Google Books lookup failed.
    // Admin has typed a title — go straight to confirm, preserving the real
    // ISBN. Server (POST /admin/books) accepts isbn+title together and skips
    // its Google Books call in that case.
    if (isbnClean && titleClean) {
      if (!isValidIsbn(isbnClean)) {
        setErrors({ isbn: "ISBN inválido (10 ou 13 dígitos)" });
        return;
      }
      setIsbn(isbnClean);
      setConfirmed({
        isbn: isbnClean,
        manual: true,
        metadata: {
          title: titleClean,
          author: author.trim() || null,
          description: null,
          genre: null,
          coverUrl: null,
          language: null,
        },
      });
      return;
    }

    // ISBN flow — call Google Books for metadata
    if (isbnClean) {
      if (!isValidIsbn(isbnClean)) {
        setErrors({ isbn: "ISBN inválido (10 ou 13 dígitos)" });
        return;
      }
      setIsbn(isbnClean);
      setLookupLoading(true);
      try {
        const r = await lookup.refetch();
        if (r.data) {
          // Local-cache hits go through the manual-data submission path so
          // server gets the metadata we just received (avoids the server
          // re-hitting Google Books and possibly getting a stale/different
          // answer or just failing).
          setConfirmed({
            isbn: r.data.isbn,
            manual: r.data.source === "local",
            metadata: r.data.metadata,
          });
        } else if (r.error) {
          setLookupFailed(true);
        }
      } finally {
        setLookupLoading(false);
      }
      return;
    }

    // Title-only flow — skip Google Books, jump straight to confirm
    if (titleClean) {
      setConfirmed({
        isbn: null,
        manual: true,
        metadata: {
          title: titleClean,
          author: author.trim() || null,
          description: null,
          genre: null,
          coverUrl: null,
          language: null,
        },
      });
      return;
    }

    setErrors({ isbn: t("admin.inventory.errIsbnOrTitle") });
  }

  function onConfirm(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    setFlash(null);
    // Accept an email OR a name — the server resolves either via
    // resolveOrCreateUser. Only require something (or the anonymous toggle).
    if (!noCredit && donorEmail.trim().length < 2) {
      setErrors({ donorEmail: t("admin.inventory.errEmail") });
      return;
    }
    if (!confirmed) return;
    const donorField = noCredit
      ? { noCredit: true }
      : { donorEmail: donorEmail.trim() };
    const body: SubmitInput = confirmed.manual
      ? {
          title: confirmed.metadata.title,
          author: confirmed.metadata.author ?? undefined,
          // Real ISBN preserved on the fallback / local-cache path so the
          // book is stored with its actual ISBN, not a 9999... placeholder.
          ...(confirmed.isbn ? { isbn: confirmed.isbn } : {}),
          // Forward cached metadata when present (came from local catalog)
          // so the new copy inherits the same genre/cover/description as
          // the prior copy of the same book.
          ...(confirmed.metadata.description
            ? { description: confirmed.metadata.description }
            : {}),
          ...(confirmed.metadata.genre ? { genre: confirmed.metadata.genre } : {}),
          ...(confirmed.metadata.coverUrl ? { coverUrl: confirmed.metadata.coverUrl } : {}),
          condition: "good" as BookCondition,
          ...donorField,
        }
      : {
          isbn: confirmed.isbn!,
          condition: "good" as BookCondition,
          ...donorField,
        };
    submit.mutate(body);
  }

  function reset() {
    setConfirmed(null);
    setIsbn("");
    setTitle("");
    setAuthor("");
    setLookupError(null);
    setLookupFailed(false);
    setNoCredit(false);
  }

  return (
    <section aria-labelledby="checkin-title" className="admin__panel inventory">
      <header className="inventory__head">
        <div>
          <h2 id="checkin-title">{t("admin.inventory.title")}</h2>
          <p>{t("admin.inventory.intro")}</p>
        </div>
        <div className="inventory__head-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setUploadOpen(true)}
          >
            {t("admin.books.importExcel")}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setPhotoOpen(true)}
          >
            {t("admin.inventory.importPhoto")}
          </button>
        </div>
      </header>

      {flash && (
        <div className={`inventory__flash inventory__flash--${flash.type}`} role="alert" aria-live="polite">
          {flash.msg}
        </div>
      )}

      {!confirmed ? (
        <div className="surface">
          <form onSubmit={onProcess} className="inventory__lookup">
            <div className="field">
              <label htmlFor="isbn">ISBN</label>
              <input
                id="isbn"
                inputMode="numeric"
                pattern="[0-9-\s]+"
                placeholder={t("admin.inventory.isbnPlaceholder")}
                value={isbn}
                onChange={(e) => {
                  setIsbn(e.target.value);
                  // Editing the ISBN invalidates any prior failed-lookup state
                  // so a fresh value gets a fresh attempt.
                  if (lookupFailed) setLookupFailed(false);
                }}
                aria-invalid={Boolean(errors.isbn)}
                aria-describedby={errors.isbn ? "isbn-error" : undefined}
              />
              {errors.isbn && (
                <p id="isbn-error" className="field__error" role="alert">{errors.isbn}</p>
              )}
            </div>

            <div className="field">
              <label htmlFor="manual-title">
                {lookupFailed ? t("admin.inventory.titleFailedLabel") : t("admin.inventory.titleLabel")}
              </label>
              <input
                id="manual-title"
                type="text"
                placeholder={t("admin.inventory.titlePlaceholder")}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={isbn.trim().length > 0 && !lookupFailed}
              />
              {lookupFailed && (
                <p className="field__help" role="status" aria-live="polite">
                  {t("admin.inventory.noGoogleData")}
                </p>
              )}
            </div>

            <div className="field">
              <label htmlFor="manual-author">{t("admin.inventory.authorLabel")}</label>
              <input
                id="manual-author"
                type="text"
                placeholder={t("admin.inventory.authorPlaceholder")}
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                disabled={isbn.trim().length > 0 && !lookupFailed}
              />
            </div>

            <div className="inventory__lookup-actions">
              <button type="submit" className="btn btn-primary" disabled={lookupLoading}>
                {lookupLoading ? t("admin.inventory.consulting") : t("admin.inventory.process")}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-icon"
                onClick={() => setScannerOpen(true)}
                aria-label={t("admin.activateCamera")}
                title={t("admin.activateCamera")}
              >
                <IconBarcode />
              </button>
            </div>

            {lookupError && (
              <p className="field__error" role="alert">{lookupError}</p>
            )}
          </form>

          {scannerOpen && (
            <IsbnScanner
              onDetected={async (detected) => {
                // Drive the lookup directly with the detected ISBN — calling
                // lookup.refetch() here would use a queryFn closure over the
                // previous `isbn` state, since setIsbn is async.
                setIsbn(detected);
                setScannerOpen(false);
                setErrors({});
                setLookupError(null);
                setLookupFailed(false);
                setLookupLoading(true);
                try {
                  const data = await api<IsbnLookupResp>(
                    `/api/admin/isbn-lookup/${encodeURIComponent(detected)}`,
                  );
                  setConfirmed({
                    isbn: data.isbn,
                    manual: data.source === "local",
                    metadata: data.metadata,
                  });
                } catch (err) {
                  // Fall back to manual entry without dead-ending — the
                  // admin can type a title and re-process. The scanned ISBN
                  // stays in the input so the fallback submission preserves it.
                  if (err instanceof ApiError) {
                    setLookupFailed(true);
                  } else {
                    setLookupError(t("admin.inventory.googleError"));
                  }
                } finally {
                  setLookupLoading(false);
                }
              }}
              onClose={() => setScannerOpen(false)}
            />
          )}
        </div>
      ) : (
        <form onSubmit={onConfirm} className="surface inventory__confirm">
          <div className="inventory__preview">
            {confirmed.metadata.coverUrl ? (
              <img
                src={confirmed.metadata.coverUrl}
                alt={t("admin.inventory.coverAlt", { title: confirmed.metadata.title })}
                className="inventory__cover"
              />
            ) : (
              <div className="inventory__cover inventory__cover--empty" aria-hidden="true">📖</div>
            )}
            <div>
              <h2>{confirmed.metadata.title}</h2>
              {confirmed.metadata.author && <p>{confirmed.metadata.author}</p>}
              {confirmed.metadata.genre && <p><em>{confirmed.metadata.genre}</em></p>}
              {confirmed.isbn && (
                <p className="inventory__isbn">ISBN: <code>{confirmed.isbn}</code></p>
              )}
              {confirmed.manual && (
                <p className="inventory__isbn"><em>{t("admin.inventory.manualEntry")}</em></p>
              )}
            </div>
          </div>

          <div className="field field--check">
            <label>
              <input
                type="checkbox"
                checked={noCredit}
                onChange={(e) => setNoCredit(e.target.checked)}
              />
              <span>{t("admin.inventory.noCredit")}</span>
            </label>
          </div>

          {!noCredit && (
            <div className="field">
              <label htmlFor="donor">{t("admin.inventory.donorLabel")}</label>
              <UserPicker
                inputId="donor"
                value={donorEmail}
                onChange={setDonorEmail}
                placeholder={t("admin.inventory.donorPlaceholder")}
                required
                ariaInvalid={Boolean(errors.donorEmail)}
                ariaDescribedBy={errors.donorEmail ? "donor-error" : "donor-help"}
              />
              <p id="donor-help" className="field__help">
                {t("admin.inventory.donorHelp")}
                {visitorEmail && (
                  <>
                    {" "}
                    {t("admin.inventory.donorPrefilled")}
                  </>
                )}
              </p>
              {errors.donorEmail && (
                <p id="donor-error" className="field__error" role="alert">{errors.donorEmail}</p>
              )}
            </div>
          )}

          <div className="inventory__actions">
            <button type="submit" className="btn btn-primary" disabled={submit.isPending}>
              {submit.isPending ? t("admin.inventory.saving") : t("admin.inventory.addBook")}
            </button>
            <button type="button" className="btn btn-secondary" onClick={reset}>
              {t("common.cancel")}
            </button>
          </div>
        </form>
      )}

      {uploadOpen && <BookUploadModal onClose={() => setUploadOpen(false)} />}
      {photoOpen && (
        <PhotoImportModal mode="checkin" onClose={() => setPhotoOpen(false)} />
      )}
    </section>
  );
}
